import React, { useMemo, useState } from "react";
import useTheme from "../../../../../common/hooks/useTheme";
import { Theme } from "../../../../../providers/ThemeProvider";
import { clusterByDay, TimelineTestEvent } from "./useTimelineEvents";
import { formatTimelineDate, TimelineWindow, xPct } from "./timeScale";
import TimelineTooltip, { TooltipEntry } from "./TimelineTooltip";
import classes from "./Timeline.module.css";

type Props = {
  label: string;
  /** Small text after the label ("events" in the combined view). */
  hint?: string;
  events: TimelineTestEvent[];
  window: TimelineWindow;
  /** The Events & Services view's thinner per-kind row. */
  compact?: boolean;
  divider?: boolean;
};

export const TESTS_ROW_HEIGHT = 52;
export const COMPACT_ROW_HEIGHT = 30;

/**
 * Test events as dots on a baseline. Tests taken the same day share one
 * marker, drawn as a count badge when there is more than one; hovering it
 * lists them all.
 */
const TestsRow = ({
  label,
  hint,
  events,
  window,
  compact = false,
  divider = true,
}: Props) => {
  const theme: Theme = useTheme();
  const [hoveredKey, setHoveredKey] = useState<string | null>(null);
  const clusters = useMemo(() => clusterByDay(events), [events]);
  const hovered = clusters.find((c) => c.key === hoveredKey) ?? null;
  const height = compact ? COMPACT_ROW_HEIGHT : TESTS_ROW_HEIGHT;

  return (
    <div
      className={`${classes.row} ${divider ? classes.rowDivider : ""}`}
      style={{ height }}
    >
      <div
        className={`${classes.label} ${compact ? classes.labelCompact : ""}`}
        style={styles.label(theme, Boolean(hovered))}
      >
        <div style={compact ? styles.compactName(theme) : styles.name(theme)}>
          {label}
          {hint && <span style={styles.hint(theme)}> {hint}</span>}
        </div>
      </div>

      <div className={classes.plot}>
        <div
          className={classes.baseline}
          style={{ backgroundColor: theme.colors_ds.NEUTRAL_BORDER_WEAK }}
        />
        {clusters.map((cluster) => {
          const isHovered = cluster.key === hoveredKey;
          const multiple = cluster.events.length > 1 && !compact;
          return (
            <div
              key={cluster.key}
              style={{
                ...styles.markerHitArea,
                left: `${xPct(cluster.t, window)}%`,
              }}
              onMouseEnter={() => setHoveredKey(cluster.key)}
              onMouseLeave={() => setHoveredKey(null)}
            >
              {multiple ? (
                <div style={styles.badge(theme, isHovered)}>
                  {cluster.events.length}
                </div>
              ) : (
                <div style={styles.dot(theme, isHovered)} />
              )}
            </div>
          );
        })}

        {hovered && (
          <TimelineTooltip
            leftPct={xPct(hovered.t, window)}
            anchorY={height / 2 - 6}
          >
            {hovered.events.map((event) => (
              <TooltipEntry
                key={event.id}
                title={event.title}
                lines={[event.category, formatTimelineDate(event.t)]}
                boxed={hovered.events.length > 1}
              />
            ))}
          </TimelineTooltip>
        )}
      </div>
    </div>
  );
};

export default TestsRow;

const styles = {
  label: (theme: Theme, highlighted: boolean) => ({
    backgroundColor: highlighted ? theme.colorTokens.P[10] : "transparent",
  }),
  name: (theme: Theme) => ({
    fontFamily: "var(--font-Medium)",
    fontSize: "14px",
    lineHeight: "20px",
    color: theme.colors_ds.NEUTRAL_TEXT_DEFAULT,
  }),
  compactName: (theme: Theme) => ({
    fontFamily: "var(--font-Regular)",
    fontSize: "11px",
    lineHeight: "16px",
    color: theme.colors_ds.NEUTRAL_TEXT_DEFAULT,
  }),
  hint: (theme: Theme) => ({
    fontFamily: "var(--font-Regular)",
    fontSize: "10px",
    color: theme.colors_ds.NEUTRAL_TEXT_WEAK,
  }),
  markerHitArea: {
    position: "absolute" as "absolute",
    top: "50%",
    transform: "translate(-50%, -50%)",
    padding: "6px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 2,
  },
  dot: (theme: Theme, hovered: boolean) => ({
    width: "6px",
    height: "6px",
    borderRadius: "50%",
    backgroundColor: theme.colorTokens.N[70],
    boxShadow: hovered ? `0 0 0 4px ${theme.colorTokens.N[50]}` : "none",
  }),
  badge: (theme: Theme, hovered: boolean) => ({
    minWidth: "14px",
    height: "12px",
    padding: "0 3px",
    boxSizing: "border-box" as "border-box",
    borderRadius: "6px",
    backgroundColor: hovered ? theme.colorTokens.N[80] : theme.colorTokens.N[70],
    color: theme.colors_ds.NEUTRAL_GENERAL_WHITE,
    fontFamily: "var(--font-Medium)",
    fontSize: "9px",
    lineHeight: "12px",
    textAlign: "center" as "center",
  }),
};
